import { SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { isEmpty, take } from 'lodash';
import { useRef, useState } from 'react';
import { Layout } from 'react-grid-layout';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTransformEffect } from 'react-zoom-pan-pinch';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { FullSizeAssemblySection } from 'src/components/fullSizeAssemblySection/FullSizeAssemblySection';
import { LayoutBuilderWithDisclaimer } from 'src/components/layoutBuilder';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { ILayout } from 'src/interfaces';
import { lang } from 'src/utils';

import {
  IDefaultSection,
  IGridSection,
  IPositionGridLines,
} from '../../ScheduleComponentPage.interface';
import { ScreenConfigModal } from '../screenConfigModal';
import { useLayoutScreenBuilder } from './hooks/LayoutScreenBuilder.hook';
import {
  formatAssemblyComponents,
  getScreenMessageAndType,
} from './LayoutScreenBuilder.helper';
import {
  ScreenBuilderWrapper,
  ScreenSectionsWrapper,
  ScreenTitleWrapper,
  SectionsWithComponentsWrapper,
  StyledAlert,
} from './LayoutScreenBuilder.styled';

export interface ILayoutScreenBuilder {
  layout: ILayout | undefined;
  screenIndex: number;
  scale: number;
  gridSections: IGridSection[];
  formMethods: UseFormReturn<FieldValues, any, undefined>;
  screenSections: (IGridSection | IDefaultSection)[];
  canDeleteCurrentSelectedSection: boolean;
  layerIndex: number;
  currentPositionGridLines: string[];
  setGridSections: React.Dispatch<React.SetStateAction<IGridSection[]>>;
  onActiveSectionChange: (
    newSectionId: string | null,
    currentSections?: IGridSection[],
    callback?: () => void,
  ) => void;
  onGridSectionsChange: (value: Layout[]) => void;
  setLayout: React.Dispatch<React.SetStateAction<ILayout | undefined>>;
  onUpdateScreenPredefinedSections: (screenConfigString: string) => void;
  onUpdatePositionsGridLines: (
    newPositionGridLines: Partial<IPositionGridLines> & { position: number },
  ) => void;
}

export const LayoutScreenBuilder = (props: ILayoutScreenBuilder) => {
  const {
    layout,
    screenIndex,
    gridSections,
    formMethods,
    screenSections,
    setGridSections,
    onActiveSectionChange,
    onGridSectionsChange,
    canDeleteCurrentSelectedSection,
  } = props;
  const viewportFitRatioRef = useRef<HTMLDivElement>(null);
  const [transformScale, setTransformScale] = useState(1);

  useTransformEffect(({ state }) => setTransformScale(state.scale));

  const {
    rows,
    cols,
    disclaimerPosition,
    disclaimerType,
    gridSectionHeight,
    gridSectionWidth,
    disclaimerHeightScaled,
    disclaimerWidth,
    screenConfigModal,
    handleOpenScreenConfigModal,
    onSaveScreenConfigModal,
    globalComponentsScreen,
  } = useLayoutScreenBuilder(props);

  const { type, message } = getScreenMessageAndType(
    layout as ILayout,
    screenIndex,
    globalComponentsScreen,
  );

  const TooltipChipZoom = 3;

  return (
    <ScreenBuilderWrapper>
      <Box sx={{ zoom: TooltipChipZoom }}>
        <StyledAlert severity={type} icon={false}>
          {message}
        </StyledAlert>
        <ScreenTitleWrapper>
          <DynamicTooltipChip chipName={`${lang.screen} ${screenIndex + 1}`} />
          {gridSections.filter((section) => !section.isDYR).length > 0 ? (
            <CustomTooltip
              title={lang.scheduleComponent.screenGridSettingTooltip}
              skipValidateOverflow={true}>
              <IconButton color="secondary" disabled>
                <SettingsIcon />
              </IconButton>
            </CustomTooltip>
          ) : (
            <IconButton color="secondary" onClick={handleOpenScreenConfigModal}>
              <SettingsIcon />
            </IconButton>
          )}
        </ScreenTitleWrapper>
      </Box>

      <ScreenSectionsWrapper ref={viewportFitRatioRef}>
        <LayoutBuilderWithDisclaimer
          dynamicRatio={TooltipChipZoom}
          gridSections={gridSections}
          rows={rows}
          cols={cols}
          cellHeight={gridSectionHeight}
          cellWidth={gridSectionWidth}
          setGridSections={setGridSections}
          isRecommendationEnabled={false}
          disclaimerPosition={disclaimerPosition!}
          disclaimerType={disclaimerType!}
          disclaimerHeightScaled={disclaimerHeightScaled!}
          disclaimerWidth={disclaimerWidth!}
          sectionFontSize={14 * TooltipChipZoom}
          onActiveSectionChange={onActiveSectionChange}
          onGridSectionsChange={onGridSectionsChange}
          canDeleteCurrentSelectedSection={canDeleteCurrentSelectedSection}
          isScheduleComponent={true}
          transformScale={transformScale}
        />

        <SectionsWithComponentsWrapper>
          {screenSections.map((section) => {
            if (isEmpty(section.components)) return null;
            const assemblyComponents = formatAssemblyComponents(
              take(section.components),
            );

            return (
              <FullSizeAssemblySection
                sectionTypes={layout?.config?.sectionTypes}
                key={section.i}
                section={{
                  id: -1,
                  name: section.name!,
                  width: section.width!,
                  height: section.height!,
                  left_offset: section.leftOffset!,
                  top_offset: section.topOffset!,
                  created_at: '',
                  modified_at: '',
                }}
                showSectionBorder={false}
                assemblyComponents={assemblyComponents}
              />
            );
          })}
        </SectionsWithComponentsWrapper>
      </ScreenSectionsWrapper>
      {screenConfigModal.open && (
        <ScreenConfigModal
          open={screenConfigModal.open}
          layout={layout}
          screenIndex={screenIndex}
          formMethods={formMethods}
          onCloseModal={screenConfigModal.onCloseModal}
          onSaveScreenConfigModal={onSaveScreenConfigModal}
        />
      )}
    </ScreenBuilderWrapper>
  );
};
